/**
 * The score's instruments as data (07 §2): a PWM lead, a filtered saw bass, arps, a pad, a
 * bugle for the memorial - played live by WebAudio oscillators - and percussion, which is the
 * sound bank's drum patches. `envelopePoints` is the pure half of "how a note is played".
 */

import type { SfxId } from '../sfx';

export type ToneWave = 'pulse25' | 'pulse12' | 'square' | 'saw' | 'triangle' | 'sine';

export interface ToneInstrument {
  kind: 'tone';
  wave: ToneWave;
  /** A second oscillator this many cents away, for thickness; 0 = a single oscillator. */
  detune: number;
  attack: number;
  decay: number;
  /** 0..1 of the peak. */
  sustain: number;
  release: number;
  /** Low-pass: resting cutoff in Hz, and how many times higher it starts each note (a pluck). */
  cutoff?: number;
  cutoffEnv?: number;
  q?: number;
  /** Vibrato, on notes of at least `minSteps` only - short notes stay rock steady. */
  vibrato?: { hz: number; cents: number; minSteps: number };
  gain: number;
}

export interface DrumInstrument {
  kind: 'drum';
  sfx: SfxId;
  gain: number;
}

export type Instrument = ToneInstrument | DrumInstrument;

export const INSTRUMENTS = {
  lead: {
    kind: 'tone', wave: 'pulse25', detune: 7, attack: 0.01, decay: 0.12, sustain: 0.7, release: 0.12, cutoff: 3800,
    vibrato: { hz: 5.5, cents: 14, minSteps: 4 }, gain: 0.2,
  },
  bass: { kind: 'tone', wave: 'saw', detune: 0, attack: 0.005, decay: 0.16, sustain: 0.55, release: 0.06, cutoff: 360,
    cutoffEnv: 5, q: 3, gain: 0.34 },
  /** Temperate's warmer bass: triangle under the saw's place, lower cutoff. */
  warmBass: { kind: 'tone', wave: 'triangle', detune: 5, attack: 0.008, decay: 0.2, sustain: 0.7, release: 0.08,
    cutoff: 700, gain: 0.4 },
  arp: { kind: 'tone', wave: 'pulse12', detune: 0, attack: 0.004, decay: 0.09, sustain: 0.25, release: 0.08,
    cutoff: 5000, gain: 0.09 },
  /** Arctic's glassy arp: a bell-like sine pair with a long ring. */
  glass: { kind: 'tone', wave: 'sine', detune: 1200, attack: 0.003, decay: 0.35, sustain: 0.12, release: 0.6,
    gain: 0.1 },
  pad: { kind: 'tone', wave: 'saw', detune: 9, attack: 1.6, decay: 1, sustain: 0.8, release: 2.4, cutoff: 420, q: 1,
    gain: 0.085 },
  bugle: {
    kind: 'tone', wave: 'triangle', detune: 3, attack: 0.04, decay: 0.25, sustain: 0.8, release: 0.45, cutoff: 2400,
    vibrato: { hz: 5, cents: 10, minSteps: 6 }, gain: 0.26,
  },
  ping: { kind: 'tone', wave: 'square', detune: 0, attack: 0.002, decay: 0.06, sustain: 0, release: 0.03, cutoff: 6000,
    gain: 0.08 },
  kick: { kind: 'drum', sfx: 'drum.kick', gain: 0.5 },
  snare: { kind: 'drum', sfx: 'drum.snare', gain: 0.42 },
  hat: { kind: 'drum', sfx: 'drum.hat', gain: 0.16 },
  tom: { kind: 'drum', sfx: 'drum.tom', gain: 0.4 },
  rim: { kind: 'drum', sfx: 'drum.rim', gain: 0.22 },
} satisfies Record<string, Instrument>;

export type InstrumentId = keyof typeof INSTRUMENTS;

export const instrument = (id: InstrumentId): Instrument => INSTRUMENTS[id];

/**
 * The amplitude envelope of one note as linear ramp targets `[t0, v0, t1, v1, ...]` written into
 * `out` (seconds from note-on, level 0..1); returns how many numbers were written. A note shorter
 * than the attack never reaches full level - it turns round where it is, so nothing ever jumps.
 */
export function envelopePoints(inst: ToneInstrument, seconds: number, out: Float64Array): number {
  const { attack, decay, sustain, release } = inst;
  let n = 0;
  const push = (t: number, v: number): void => {
    out[n++] = t;
    out[n++] = v;
  };
  push(0, 0);
  if (seconds <= attack) {
    push(seconds, seconds / attack);
  } else {
    push(attack, 1);
    if (seconds <= attack + decay) {
      push(seconds, 1 - (1 - sustain) * ((seconds - attack) / decay));
    } else {
      push(attack + decay, sustain);
      push(seconds, sustain);
    }
  }
  push(seconds + release, 0);
  return n;
}

/** Room for the longest envelope `envelopePoints` writes. */
export const ENVELOPE_SLOTS = 10;
